/* eslint-disable no-nested-ternary, prefer-const, no-restricted-properties */
import { get, isArray, isEmpty, isNil, sumBy } from 'lodash'

export function calculateFees(products) {
  return (products || []).map(product => {
    const { amount, totalPrice, fee, feeAmount, feePcAmountCombinationRule, feeTax, surcharge, orgTotalPrice, markAs } =
      product

    const orderItemTotalPrice = isNil(totalPrice) ? amount : totalPrice

    const markAsPaid = !isNil(markAs) && markAs === 'Paid'

    let chargeTaxAmount = 0
    let chargeAmount = 0 // processing fee, if surcharge is defined, then member pays the surcharge. If not, then org pays.

    const feePercentageValue = orderItemTotalPrice * (fee / 100)
    const feeAmountValue = (orderItemTotalPrice * feeAmount) / orgTotalPrice

    // if both fee (%) and fee amount are defined in the rule
    if (!isNil(fee) && !isNil(feeAmount)) {
      switch (feePcAmountCombinationRule) {
        case 'Higher Value':
          chargeAmount = Math.max(feePercentageValue, feeAmountValue)
          break
        case 'Lower Value':
          chargeAmount = Math.min(feePercentageValue, feeAmountValue)
          break
        case 'Combination': // yet to implement, not scoped
          break
        default:
          break
      }
    } else if (!isNil(fee)) {
      // if only fee (%) is defined in the rule
      chargeAmount = feePercentageValue
    } else if (!isNil(feeAmount)) {
      // if only fee amount is defined in the rule
      chargeAmount = feeAmountValue
    }

    chargeTaxAmount = !isNil(feeTax) ? chargeAmount * (feeTax / 100) : 0
    const surchargeAmount = !isNil(surcharge) ? (surcharge / 100) * (chargeAmount + chargeTaxAmount) : 0
    const processingFee = chargeAmount + chargeTaxAmount

    return {
      ...product,
      charge: markAsPaid ? 0 : Number(precise_round(chargeAmount, 4)),
      chargeTax: markAsPaid ? 0 : Number(precise_round(chargeTaxAmount, 4)),
      surchargeAmount: markAsPaid ? 0 : Number(precise_round(surchargeAmount, 4)),
      processingFee: markAsPaid ? 0 : Number(precise_round(processingFee, 2)),
    }
  })
}

export function precise_round(num, dec) {
  if (typeof num !== 'number' || typeof dec !== 'number') {
    return false
  }
  const num_sign = num >= 0 ? 1 : -1
  return (Math.round(num * 10 ** dec + num_sign * 0.0001) / 10 ** dec).toFixed(dec)
}

// Turn precise_round result to number type
export function precise_round_to_number(num, dec) {
  return Number(precise_round(num, dec))
}

export function processProducts(source, products, feeConfigs = [], paymentGateway, markAs) {
  const errorMessage =
    source === 'Admin Portal'
      ? "The amount doesn't match any processing fee rules."
      : "The amount doesn't match any processing fee rules. Please check with the organisation administrator."
  const orderTotalPrice = Number(precise_round(sumBy(products, 'totalPrice'), 2))

  let error =
    orderTotalPrice > 0 && (isEmpty(feeConfigs) || isNil(feeConfigs))
      ? {
          error: true,
          errorMessage,
        }
      : {}

  const processedProducts = (products || []).map(product => {
    const {
      id,
      formProductId,
      name,
      organisation,
      listPrice,
      quantity,
      totalPrice,
      memberId,
      grantMemberTypes,
      type,
      image,
      deliveryMethod,
      sku,
      productAttributes,
      attributeId,
      attributeValue,
      optionIds,
      isForSubEvent,
      voucher,
      discount,
      productPromoCode,
      orgName,
      gstApplicable,
    } = product

    const orgTotalPrice = Number(
      precise_round(
        sumBy(
          products.filter(p => p.organisation === organisation),
          'totalPrice'
        ),
        2
      )
    )

    const feeConfigRules =
      feeConfigs && !isNil(feeConfigs[0])
        ? paymentGateway.name.toLowerCase() === 'paypal'
          ? (feeConfigs || []).filter(
              config => config.organisation === organisation && config.paymentGateway.name.toLowerCase() === 'braintree'
            )
          : (feeConfigs || []).filter(
              config => config.organisation === organisation && config.paymentGateway.name === paymentGateway.name
            )
        : {}
    if (orgTotalPrice > 0 && isEmpty(feeConfigRules)) {
      error = {
        error: true,
        errorMessage,
      }
    }

    let feeConfig = isArray(feeConfigRules) ? (feeConfigRules.length === 1 ? feeConfigRules[0] : {}) : feeConfigRules

    if ((isNil(feeConfig) || isEmpty(feeConfig)) && !isEmpty(feeConfigRules)) {
      feeConfigRules.forEach(config => {
        if (config.minimumTransactionAmount && config.maximumTransactionAmount) {
          if (config.minimumTransactionAmount <= orgTotalPrice && config.maximumTransactionAmount >= orgTotalPrice) {
            feeConfig = { ...config }
          }
        } else if (config.minimumTransactionAmount) {
          if (config.minimumTransactionAmount <= orgTotalPrice) {
            feeConfig = { ...config }
          }
        } else if (config.maximumTransactionAmount) {
          if (config.maximumTransactionAmount >= orgTotalPrice) {
            feeConfig = { ...config }
          }
        }
      })
    }

    if ((isNil(feeConfig) || isEmpty(feeConfig)) && orgTotalPrice > 0) {
      error = {
        error: true,
        errorMessage,
      }
    }

    const { fee, feeAmount, feePCAmount, feeTax, surcharge } = feeConfig

    const calculatedFee = calculateFees([
      {
        totalPrice,
        fee,
        feeAmount,
        feeTax,
        feePcAmountCombinationRule: feePCAmount,
        surcharge,
        orgTotalPrice,
        markAs,
        discount,
      },
    ])[0]

    const { charge, chargeTax, processingFee, surchargeAmount } = calculatedFee

    const { promoCodeId: promoCode, promoCodeAmount } = productPromoCode || {}

    return {
      id,
      formProductId,
      orgName,
      quantity: quantity || 1,
      name,
      listPrice,
      totalPrice,
      orgId: organisation,
      charge: feeAmount ? Number(precise_round((totalPrice * feeAmount) / orgTotalPrice, 2)) : charge,
      chargeTax,
      surcharge: surchargeAmount || 0,
      processingFee,
      memberId,
      grantMemberTypes,
      type,
      image,
      deliveryMethod,
      sku,
      productAttributes,
      attributeId,
      attributeValue,
      optionIds,
      isForSubEvent,
      voucher,
      ...(voucher && { ...voucher }),
      discount,
      productPromoCode,
      promoCode,
      promoCodeAmount,
      gstApplicable,
    }
  })

  return { processedProducts, error }
}

/**
 * Calculate the tax amount included in a price using reverse tax formula.
 * Tax is already included in totalPrice - this extracts the components.
 * @param {number} totalPrice - The total price (tax-inclusive)
 * @param {number} taxRate - Tax rate as percentage (e.g., 10 for 10%)
 * @returns {{ priceExTax: number, taxAmount: number }}
 */
export function calculateIncludedTax(totalPrice, taxRate) {
  if (!taxRate || taxRate <= 0 || !totalPrice || totalPrice <= 0) {
    return { priceExTax: totalPrice || 0, taxAmount: 0 }
  }
  const priceExTax = Number(precise_round(totalPrice / (1 + taxRate / 100), 2))
  const taxAmount = Number(precise_round(totalPrice - priceExTax, 2))
  return { priceExTax, taxAmount }
}

export function getProductChildMap(products = []) {
  return products.reduce((a, p) => {
    const depProducts = p.dependentProducts || []
    if (depProducts.length < 1) {
      return a
    }
    depProducts.forEach(dp => {
      const dpId = dp.primaryProduct
      const oldDpList = get(a, dpId) || {}
      a[dpId] = {
        ...oldDpList,
        [p.sfid]: {
          isMandatory: p.isMandatory,
          mustBePaid: dp.mustBePaid,
        },
      }
    })
    return a
  }, {})
}

export function getOrderItemGroupMap(products = [], subEvents = []) {
  const subEventNames = subEvents.reduce((a, s) => {
    const prods = s.products.map(p => p.formProductId)
    a[s.name] = prods
    return a
  }, {})

  const addOn = []
  const event = []
  const membership = []
  products.forEach(p => {
    p.type === 'Add-on' && addOn.push(p)
    if (p.type === 'Event' && !p.isForSubEvent) {
      event.push(p)
    }
    p.type === 'Membership' && membership.push(p)
  })

  return {
    Membership: membership,
    Event: event,
    ...subEventNames,
    'Add-on': addOn,
  }
}
